import styled from '@emotion/styled';
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import CustomDateRangePicker from '~/components/CustomDateRangePicker';
import DropdownInput from '~/components/DropdownInput';
import Gap from '~/components/Gap';
import Text from '~/components/Text';
import InfoIcon from '~/icons/InfoIcon';
import StatusGuidePopupComponent from './StatusGuidePopup';
import { parseDateSearchParam } from '~/utils/datetime';
import { useInvoiceListContext } from '../hooks/useInvoiceListContext';
import { CURRENCY_LIST } from '~/utils/amount';

const statusOptions = [
  { label: 'All invoices', value: '' },
  { label: 'Draft', value: 'Draft' },
  { label: 'Sent', value: 'Sent' },
  { label: 'Overdue', value: 'Overdue' },
  { label: 'On Time', value: 'On Time' },
  { label: 'Outstanding', value: 'Outstanding' },
  { label: 'Paid', value: 'Paid' },
  { label: 'Archived', value: 'Archived' },
];

const Wrapper = styled.div`
  margin-bottom: 30px;

  .filter-dd {
    min-width: 224px;
    max-width: 224px;
  }
`;

const StatusFilterWrapper = styled.div`
  position: relative;
`;

const StatusLabelWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  margin-bottom: 4px;
`;

const InfoButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 2px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background-color: #f5f5f5;
  }
`;

const InvoiceListFilters = () => {
  const [searchParams] = useSearchParams();
  const [showStatusGuide, setShowStatusGuide] = useState(false);
  const { handleStatusChange } = useInvoiceListContext();

  return (
    <Wrapper className="d-flex">
      <StatusFilterWrapper>
        <StatusLabelWrapper>
          <Text size="xs">Status</Text>
          <InfoButton
            type="button"
            onClick={() => setShowStatusGuide(true)}
            title="Invoice Status Guide"
          >
            <InfoIcon size={12} fill="#666" />
          </InfoButton>
        </StatusLabelWrapper>
        <DropdownInput
          value={searchParams.get('status') || ''}
          options={statusOptions}
          onChange={(newVal: string) => {
            handleStatusChange('status', newVal);
          }}
          search={false}
          className="filter-dd"
        />

        {showStatusGuide && <StatusGuidePopupComponent onClose={() => setShowStatusGuide(false)} />}
      </StatusFilterWrapper>

      <Gap r={24} />

      <div>
        <Text size="xs">Date</Text>
        <Gap t={4} />
        <CustomDateRangePicker
          onChange={(startDate, endDate) => {
            handleStatusChange('created_on', `${startDate},${endDate}`);
          }}
          onClear={() => {
            handleStatusChange('created_on', '');
          }}
          value={parseDateSearchParam(searchParams.get('created_on'))}
        />
      </div>

      <Gap r={24} />

      <div>
        <Text size="xs">Currency</Text>
        <Gap t={4} />
        <DropdownInput
          value={searchParams.get('currency') || ''}
          options={[{ label: 'All Currencies', value: '', symbol: '' }, ...CURRENCY_LIST]}
          onChange={(newVal: string) => {
            handleStatusChange('currency', newVal);
          }}
          search={false}
          className="filter-dd"
        />
      </div>
    </Wrapper>
  );
};

export default InvoiceListFilters;
